import AppUser from '@/entities/AppUser';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, database } from './FirebaseProvider';

export interface RemoteUserData {
  firstName: string;
  lastName: string;
  googleApiKey: string;
  userId: string;
}

export interface RemoteUser {
  id?: string;
  data: RemoteUserData;
}

export const mapAppUser = (appUser: AppUser): RemoteUser => {
  let remoteUser: RemoteUser = {
    id: appUser.firestoreId,
    data: {
      firstName: appUser.firstName || '',
      lastName: appUser.lastName || '',
      googleApiKey: appUser.googleApiKey || '',
      userId: appUser.firestoreUserId || '',
    },
  };

  return remoteUser;
};

export const mapRemoteUser = (remoteUser: RemoteUser): AppUser => {
  return {
    firestoreId: remoteUser.id,
    firstName: remoteUser.data.firstName,
    lastName: remoteUser.data.lastName,
    googleApiKey: remoteUser.data.googleApiKey,
    firestoreUserId: remoteUser.data.userId,
  };
};

export async function getAppUser(): Promise<AppUser | null> {
  let appUser: AppUser | null = null;

  try {
    if (auth.currentUser) {
      const userCollection = collection(database, 'user');
      const q = query(
        userCollection,
        where('userId', '==', auth.currentUser.uid),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        const id = querySnapshot.docs[0].id;

        const remoteUser: RemoteUser = {
          id: id,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            googleApiKey: data.googleApiKey,
            userId: data.userId,
          },
        };

        if (remoteUser) {
          appUser = mapRemoteUser(remoteUser);
        }
      }
    }

    return appUser;
  } catch (error) {
    throw error;
  }
}

export async function updateRemoteUser(
  appUser: AppUser,
  updatedFields: (keyof RemoteUserData)[],
): Promise<void> {
  try {
    const remoteUser = mapAppUser(appUser);
    if (remoteUser.id) {
      const userDocRef = doc(database, 'user', remoteUser.id);

      let transformedField: any = {};
      updatedFields.forEach((updatedField) => {
        transformedField[updatedField] = remoteUser.data[updatedField];
      });

      await updateDoc(userDocRef, transformedField);
    } else {
      throw new Error('Can not update user with empty firebase id');
    }
  } catch (error) {
    throw error;
  }
}

export async function createRemoteUser(appUser: AppUser): Promise<AppUser> {
  try {
    const userDocRef = doc(collection(database, 'user'));
    const userId = userDocRef.id;
    const updatedAppUser = { ...appUser, firestoreId: userId };
    const remoteUser = mapAppUser(updatedAppUser);
    await setDoc(userDocRef, remoteUser.data);
    return updatedAppUser;
  } catch (error) {
    throw error;
  }
}
